import { createNoise2D, type NoiseFunction2D } from "simplex-noise";
import {
    GRID_SIZE,
    HEIGHT_MIN,
    HEIGHT_MAX,
    NOISE_SCALE,
    FBM_OCTAVES,
    FBM_LACUNARITY,
    FBM_GAIN,
    HEIGHT_EASING_ITERATIONS,
    DOMAIN_WARP_AMP,
    DOMAIN_WARP_FREQ,
} from "../config";

function mulberry32(seed: number) {
    return function () {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export function makeNoise2D(seed: number): NoiseFunction2D {
    return createNoise2D(mulberry32(seed));
}

/**
 * Fractal Brownian Motion. Returns a value in roughly [-1, 1].
 */
export function fbm(
    noise: NoiseFunction2D,
    x: number,
    y: number,
    octaves: number = FBM_OCTAVES,
    lacunarity: number = FBM_LACUNARITY,
    gain: number = FBM_GAIN,
): number {
    let amp = 1;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let i = 0; i < octaves; i++) {
        sum += noise(x * freq, y * freq) * amp;
        norm += amp;
        amp *= gain;
        freq *= lacunarity;
    }
    return norm > 0 ? sum / norm : 0;
}

export function generateHeightmap(
    seed: number,
    size = GRID_SIZE,
    minH = HEIGHT_MIN,
    maxH = HEIGHT_MAX,
) {
    const heightNoise = makeNoise2D(seed);
    const warpNoise = DOMAIN_WARP_AMP > 0 ? makeNoise2D(seed + 31337) : null;
    const scale = NOISE_SCALE;
    const heights = Array.from({ length: size }, () => Array(size).fill(0));

    for (let x = 0; x < size; x++) {
        for (let z = 0; z < size; z++) {
            let sx = x * scale;
            let sz = z * scale;
            if (warpNoise) {
                sx += warpNoise(x * DOMAIN_WARP_FREQ, z * DOMAIN_WARP_FREQ) *
                    DOMAIN_WARP_AMP * scale;
                sz += warpNoise(x * DOMAIN_WARP_FREQ + 100, z * DOMAIN_WARP_FREQ + 100) *
                    DOMAIN_WARP_AMP * scale;
            }
            const n = fbm(heightNoise, sx, sz);
            let t = (n + 1) / 2;
            for (let i = 0; i < HEIGHT_EASING_ITERATIONS; i++) {
                t = t * t * (3 - 2 * t);
            }
            heights[x][z] = Math.round(minH + t * (maxH - minH));
        }
    }
    return heights;
}

export function smoothHeightmap(heights: number[][], passes = 1) {
    const N = heights.length;
    for (let p = 0; p < passes; p++) {
        for (let x = 1; x < N - 1; x++) {
            for (let z = 1; z < N - 1; z++) {
                heights[x][z] = Math.round(
                    (heights[x][z] +
                        heights[x - 1][z] +
                        heights[x + 1][z] +
                        heights[x][z - 1] +
                        heights[x][z + 1]) /
                    5,
                );
            }
        }
    }
    return heights;
}
